import type { FC } from 'react'
import { Droppable } from '@/components/ui/dnd-atoms'
import { Skeleton } from '@/components/ui/skeleton'
import { DeallocateImageButton } from '@/features/photoset/components/deallocate-image-button'
import {
	SpaceCardEmptyPreviewSlot,
	SpaceCardHeader,
	SpaceCardImagePreviewSlot,
	SpaceCardImagesPreviewGrid,
	SpaceCardWrapper
} from '@/features/photoset/components/space-card-atoms'
import type { Image, PhotosetSpace } from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

interface SpaceCardProps {
	asSkeleton?: undefined
	id: PhotosetSpace['id']
	spaceImages: Image[]
	shortcut: string
	title: string
	openSpaceDetailModal: () => void
	photosetSpaceId: number
}

interface SpaceCardSkeletonProps {
	asSkeleton: true
	id?: undefined
	spaceImages?: undefined
	shortcut?: undefined
	title?: undefined
	openSpaceDetailModal?: undefined
	photosetSpaceId?: number
}

export const SpaceCard: FC<SpaceCardProps | SpaceCardSkeletonProps> = ({
	photosetSpaceId,
	spaceImages,
	id,
	shortcut,
	title,
	openSpaceDetailModal,
	asSkeleton
}) => {
	const { t } = useTranslation()
	const imagesInPreview = spaceImages?.slice(-5).reverse() ?? []

	if (asSkeleton) {
		return <Skeleton height='128px' width='100%' />
	}

	return (
		<SpaceCardWrapper hasImages={spaceImages.length >= 1}>
			<SpaceCardHeader
				headerTitle={title}
				images={spaceImages}
				openSpaceDetailModal={openSpaceDetailModal}
				shortcut={shortcut}
			/>
			<Droppable id={id.toString()}>
				{(droppable) =>
					spaceImages.length >= 1 ? (
						<SpaceCardImagesPreviewGrid
							$isDraggingOver={droppable.isOver}
							ref={droppable.setNodeRef}
						>
							{imagesInPreview.map((image) => {
								const otherImagesCount =
									spaceImages.length - imagesInPreview.length
								return (
									<SpaceCardImagePreviewSlot
										image={image}
										key={image.id}
										otherImagesCount={otherImagesCount}
										overlayComponent={
											<DeallocateImageButton
												image={image}
												keepSelected={false}
												photosetSpaceId={
													photosetSpaceId
												}
												showToast={false}
												trackInHistory={true}
											/>
										}
									/>
								)
							})}

							{Array.from(
								{
									length: 5 - imagesInPreview.length
								},
								(_, i) => `empty-slot-${i}`
							).map((key) => (
								<SpaceCardEmptyPreviewSlot key={key} />
							))}
						</SpaceCardImagesPreviewGrid>
					) : (
						<div
							className={`flex w-full justify-center rounded-lg border border-neutral-100 bg-neutral-50 py-[22px] transition-all duration-150 ease-in-out ${
								droppable.isOver
									? 'shadow-[0_0_0_4px_var(--color-blue-100)]'
									: ''
							}`}
							ref={droppable.setNodeRef}
						>
							<h3 className='font-medium text-neutral-600 text-sm'>
								{t(
									'photoset:allocation.space_dropzone_description'
								)}
							</h3>
						</div>
					)
				}
			</Droppable>
		</SpaceCardWrapper>
	)
}

SpaceCard.displayName = 'SpaceCard'
